import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { adminApi, type BatchSummary, type OrgSummary } from '@/lib/api/admin';
import { toast } from 'sonner';
import { DatePicker } from '@/components/ui/date-picker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export function CloneBatchModal({
  sourceBatch,
  orgs,
  onClose,
  onCloned,
}: {
  sourceBatch: BatchSummary;
  orgs: OrgSummary[];
  onClose: () => void;
  onCloned: (newBatch: BatchSummary) => void;
}) {
  const navigate = useNavigate();
  const [orgId, setOrgId]         = useState(sourceBatch.org_id);
  const [name, setName]           = useState(`${sourceBatch.name} — Clone`);
  const [startDate, setStartDate] = useState(sourceBatch.start_date ?? '');
  const [endDate, setEndDate]     = useState(sourceBatch.end_date ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState('');

  // Reset fields if the source batch changes (modal re-used for different rows).
  useEffect(() => {
    setOrgId(sourceBatch.org_id);
    setName(`${sourceBatch.name} — Clone`);
    setStartDate(sourceBatch.start_date ?? '');
    setEndDate(sourceBatch.end_date ?? '');
    setError('');
  }, [sourceBatch.id]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (startDate && endDate && endDate < startDate) {
      setError('End date must be after start date');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const newBatch = await adminApi.cloneBatch(sourceBatch.id, {
        org_id: orgId,
        name: name.trim(),
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      });
      toast.success(`"${newBatch.name}" created`);
      onCloned(newBatch);
      navigate({ to: '/admin/batches/$batchId', params: { batchId: newBatch.id } });
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Failed to clone batch');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md p-0" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader className="border-b border-border px-5 py-4">
          <DialogTitle className="text-[15px]">Clone batch</DialogTitle>
          <p className="text-[11px] text-muted-foreground">
            Copies curriculum, sessions, resources, and assessments. Students and trainers are not copied.
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 px-5 py-5">
            <div>
              <label className="mb-1.5 block text-[13px] font-medium text-foreground">
                Organization
              </label>
              <Select value={orgId} onValueChange={setOrgId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select org…" />
                </SelectTrigger>
                <SelectContent>
                  {orgs.map((o) => (
                    <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="mb-1.5 block text-[13px] font-medium text-foreground">
                Batch name
              </label>
              <Input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. DSA + CP — Cohort 2"
                maxLength={200}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-foreground">Start date</label>
                <DatePicker value={startDate} onChange={setStartDate} />
              </div>
              <div>
                <label className="mb-1.5 block text-[13px] font-medium text-foreground">End date</label>
                <DatePicker value={endDate} onChange={setEndDate} />
              </div>
            </div>

            {error && <p className="text-[12px] text-destructive">{error}</p>}
          </div>

          <DialogFooter className="border-t border-border px-5 py-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !orgId || !name.trim()}>
              {submitting ? (
                <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Cloning…</>
              ) : (
                'Clone batch'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
